import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, RefreshCw, PlusCircle, CheckCircle2, ArrowRight } from "lucide-react";

export default function MasterImport() {
  const [file, setFile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (commit) => {
    if (!file) return toast.error("Choose a .zip release file first.");
    setBusy(true);
    if (commit) setResult(null); else { setPlan(null); setResult(null); }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/master-import", fd, {
        params: { commit },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      if (commit) {
        setResult(data);
        toast.success(`Applied — ${data.refreshed} refreshed, ${data.new} new.`);
      } else {
        setPlan(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed. Check the file and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="master-import-page" className="max-w-4xl">
      <h1 className="font-heading font-black text-3xl tracking-tight">Master Release Import</h1>
      <p className="text-muted-foreground mt-2">
        Upload a compliant release ZIP (master PDFs + optional register CSV). It matches each file to your
        catalog by code and title, <strong>replaces matches in place</strong> (keeping links & entitlements)
        and <strong>adds anything new</strong>. Run it here or on the live site — same result.
      </p>

      <div className="mt-8 border-2 border-dashed border-border bg-white p-8 text-center">
        <UploadCloud className="w-8 h-8 text-primary mx-auto" />
        <input type="file" accept=".zip" data-testid="master-import-file"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setPlan(null); setResult(null); }}
          className="mt-4 block mx-auto text-sm" />
        {file && <p className="text-sm text-muted-foreground mt-2">{file.name} · {(file.size / 1048576).toFixed(1)} MB</p>}
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <button onClick={() => send(false)} disabled={busy || !file} data-testid="master-import-analyze"
            className="inline-flex items-center gap-2 border border-border px-5 py-2.5 font-semibold hover:bg-secondary disabled:opacity-50">
            {busy && !result ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Analyze (preview)
          </button>
          <button onClick={() => send(true)} disabled={busy || !plan} data-testid="master-import-apply"
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-semibold btn-lift disabled:opacity-50">
            Apply changes <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {plan && !result && <p className="text-xs text-muted-foreground mt-3">Review the plan below, then click <strong>Apply changes</strong>.</p>}
      </div>

      {plan && (
        <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="master-import-plan">
          <Stat icon={RefreshCw} label="Will refresh in place" value={plan.refresh} />
          <Stat icon={PlusCircle} label="Will add as new" value={plan.new} />
          <Stat icon={CheckCircle2} label="Catalog after" value={`${plan.current_total} → ${plan.projected_total}`} />
        </div>
      )}

      {plan && plan.renamed?.length > 0 && (
        <Section title={`Renamed / re-coded (${plan.renamed.length})`}>
          <Table rows={plan.renamed.map((r) => [r.old_code || "—", r.new_code, r.title])}
            head={["Old code", "New master code", "Document"]} testid="master-import-renamed" />
        </Section>
      )}

      {plan && plan.new_docs?.length > 0 && (
        <Section title={`New documents (${plan.new_docs.length})`}>
          <Table rows={plan.new_docs.map((r) => [r.code, r.title])}
            head={["Code", "Document"]} testid="master-import-newdocs" />
        </Section>
      )}

      {result && (
        <div className="mt-8 bg-green-50 border border-green-200 p-6" data-testid="master-import-result">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="w-5 h-5" />
            <h3 className="font-heading font-bold">Import applied</h3>
          </div>
          <p className="text-sm text-green-900 mt-2">
            {result.refreshed} refreshed · {result.new} added · {result.failed} failed · catalog now <strong>{result.total_after}</strong> documents.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-border p-5">
      <Icon className="w-5 h-5 text-primary mb-3" />
      <div className="font-heading font-black text-2xl">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="font-heading font-bold text-sm mb-3">{title}</h2>
      <div className="bg-white border border-border overflow-x-auto">{children}</div>
    </div>
  );
}

function Table({ head, rows, testid }) {
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <thead>
        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
          {head.map((h) => <th key={h} className="py-2 px-4 font-semibold">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-border last:border-0">
            {r.map((c, j) => <td key={j} className={`py-2 px-4 ${j === 0 || (head.length === 3 && j === 1) ? "font-mono text-xs" : ""}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
